/**
 * MailRegisterPage — top-level shell for the mail-register wizard.
 *
 * Reads `:draftId` from the route to either:
 *   - Resume an existing draft (server fetch + hydrate working state).
 *   - Create a new draft on mount (POST /v1/drafts then redirect to the
 *     resume URL so the URL is shareable / refreshable).
 *
 * Local UI step state is mirrored to the server via the autosave debounce
 * inside `useMailRegisterDraft`.
 */
import { type JSX, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import type {
  DraftStep,
  PackagePayload,
  PartyPayload,
  PricingPayload,
  ServiceClass,
  SubmitDraftResult,
} from '../../lib/api/types';

import { MailRegisterStepper } from './MailRegisterStepper';
import { PackageStep } from './PackageStep';
import { PricingStep } from './PricingStep';
import { ReceiverStep } from './ReceiverStep';
import { ReviewStep } from './ReviewStep';
import { SenderStep } from './SenderStep';
import { SubmitOutcomePanel } from './SubmitOutcomePanel';
import { useMailRegisterDraft } from './useMailRegisterDraft';

export function MailRegisterPage(): JSX.Element {
  const { draftId } = useParams<{ draftId?: string }>();
  const navigate = useNavigate();

  const draftState = useMailRegisterDraft({
    draftId: draftId ?? null,
  });

  const [outcome, setOutcome] = useState<SubmitDraftResult | null>(null);

  // When a freshly-created draft lands, swap the URL to /:draftId so refresh
  // resumes correctly.
  useEffect(() => {
    if (draftId === undefined && draftState.draft?.id) {
      navigate(`/mail/register/${draftState.draft.id}`, { replace: true });
    }
  }, [draftId, draftState.draft?.id, navigate]);

  const handleNavigateStep = async (step: DraftStep): Promise<void> => {
    await draftState.setStep(step);
  };

  const handleSubmit = async (
    args: { acknowledgedSoftWarnings?: boolean } = {},
  ): Promise<void> => {
    try {
      const result = await draftState.submit(args);
      setOutcome(result);
    } catch (err) {
      // Submit error (network/validation/permission) — surface inline. The
      // outcome panel is reserved for the typed 422 + 200 responses.
      setOutcome(null);
      console.error('mail-register submit failed', err);
    }
  };

  const handleRegisterAnother = (): void => {
    setOutcome(null);
    navigate('/mail/register');
  };

  const handleEditDraft = (): void => {
    setOutcome(null);
  };

  const handleAcknowledgeAndResubmit = async (): Promise<void> => {
    await handleSubmit({ acknowledgedSoftWarnings: true });
  };

  if (outcome !== null) {
    return (
      <div className="space-y-6">
        <SubmitOutcomePanel
          result={outcome}
          payload={draftState.payload}
          onRegisterAnother={handleRegisterAnother}
          onEditDraft={handleEditDraft}
          onAcknowledgeAndResubmit={() => void handleAcknowledgeAndResubmit()}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="mail-register-page">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Register mail</h1>
          {draftState.draft && (
            <p className="text-xs text-slate-500">
              Draft {draftState.draft.draftRef}
              {draftState.isSaving && ' · Saving…'}
            </p>
          )}
        </div>
      </header>

      <MailRegisterStepper
        current={draftState.step}
        onNavigate={(step) => void handleNavigateStep(step)}
      />

      {draftState.step === 'SENDER' && (
        <SenderStep
          value={(draftState.payload.sender ?? {}) as Partial<PartyPayload>}
          onChange={(next) => draftState.updatePayload({ sender: next as PartyPayload })}
          onContinue={() => void handleNavigateStep('RECEIVER')}
        />
      )}

      {draftState.step === 'RECEIVER' && (
        <ReceiverStep
          value={(draftState.payload.receiver ?? {}) as Partial<PartyPayload>}
          onChange={(next) => draftState.updatePayload({ receiver: next as PartyPayload })}
          onContinue={() => void handleNavigateStep('PACKAGE')}
          onBack={() => void handleNavigateStep('SENDER')}
        />
      )}

      {draftState.step === 'PACKAGE' && (
        <PackageStep
          value={(draftState.payload.package ?? {}) as Partial<PackagePayload>}
          serviceClass={draftState.payload.serviceClass}
          onChange={(next) => draftState.updatePayload({ package: next as PackagePayload })}
          onServiceClassChange={(sc: ServiceClass) =>
            draftState.updatePayload({ serviceClass: sc })
          }
          onContinue={() => void handleNavigateStep('PRICING')}
          onBack={() => void handleNavigateStep('RECEIVER')}
        />
      )}

      {draftState.step === 'PRICING' && (
        <PricingStep
          sender={draftState.payload.sender}
          receiver={draftState.payload.receiver}
          pkg={draftState.payload.package}
          serviceClass={draftState.payload.serviceClass}
          onLockQuote={(pricing: PricingPayload) => {
            draftState.updatePayload({ pricing });
            void handleNavigateStep('REVIEW');
          }}
          onBack={() => void handleNavigateStep('PACKAGE')}
        />
      )}

      {draftState.step === 'REVIEW' && (
        <ReviewStep
          payload={draftState.payload}
          onSubmit={() => void handleSubmit()}
          onBack={() => void handleNavigateStep('PRICING')}
          isSubmitting={draftState.isSubmitting}
          errorMessage={
            draftState.submitError instanceof Error
              ? draftState.submitError.message
              : undefined
          }
        />
      )}
    </div>
  );
}
